import ActionTextIcon from "@/components/icons/ActionTextIcon";
import AntIcon from "@/components/icons/AntIcon";
import InfoIcon from "@/components/icons/InfoIcon";
import PopconfirmDeleteIcon from "@/components/icons/PopconfirmDeleteIcon";
import StatusTag from "@/components/tags/StatusTag";
import Ellipsis from "@/components/views/Ellipsis";
import { paginationTableProps, tableStandardProps } from "@/utils/componentUtils";
import { formatLocaleCurrency, formatLocaleDate, formatLocalePercentageNumber } from "@/utils/formatUtils";
import { URLQuery } from "@/utils/queryUtils";
import { isDefined, numberOrZero } from "@/utils/utils";
import { Button, Divider, Popconfirm, Space, Table } from "antd";
import { ColumnsType } from "antd/lib/table";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { generatePath, Link, useNavigate } from "react-router";
import {
  useCreateDuplicatedCommission,
  useDeleteCommission,
  useDeleteDuplicatedCommission,
  useFilterCommissions,
  useFilterPostponedCommissions,
  useRecalculateCommissionsForContract,
  useTryToIncludeCommission,
} from "../../../../../common/api/queries";
import { TableSizes } from "../../../../../common/constants";
import { Permission } from "../../../../../common/security/enums";
import type { UUID } from "../../../../../typings/global";
import { useHasAnyPermission, useHasPermissions } from "../../../../auth/hooks";
import { CONTRACT_ROUTE_PATHS } from "../../../../contract/paths";
import { PostponedCommissionsReport } from "../../../postponed/enums";
import { PostponedCommissionsFilterPageRequest, PostponedCommissionsFilterPageResult } from "../../../postponed/types";
import { isDuplicatedCommission, isSplitAddedCommission } from "../../../utils";
import { CommissionsBatchStep, CommissionType, PostponementReason } from "../../enums";
import { COMMISSIONS_BATCH_ROUTE_PATHS } from "../../paths";
import { Commission, CommissionsBatchBase, CommissionsFilterPageRequest } from "../../types";
import { CommissionKindTag } from "../CommissionKindTag";
import { CommissionForm } from "../forms/CommissionForm";
import { CommissionPostponeForm } from "../forms/CommissionPostponeForm";
import PostponementReasonTag from "../PostponementReasonTag";
import { BatchCommissionExtendedTable } from "./BatchCommissionExtendedTable";

type CommissionInfoProps = {
  commission: Commission;
  viewType?: "default" | "postponed" | "calculated";
};

const CommissionInfo = ({ commission, viewType }: CommissionInfoProps) => {
  const { t } = useTranslation();

  if ((viewType === "postponed" || viewType === "calculated") && isDuplicatedCommission(commission)) {
    return (
      <InfoIcon
        tooltip={
          <>
            <span>{t("commissions.postponed.helpers.postponedCreatedInBatch")}: </span>
            <Link
              to={generatePath(COMMISSIONS_BATCH_ROUTE_PATHS.detail.to, { id: commission.duplicateOf.batch.id })}
              target="_blank"
            >
              {commission.duplicateOf.batch.name}
            </Link>
          </>
        }
      />
    );
  } else if (viewType === "postponed") {
    return (
      <InfoIcon
        tooltip={
          <>
            <span>{t("commissions.postponed.helpers.postponedCreatedInBatch")}: </span>
            <Link
              to={generatePath(COMMISSIONS_BATCH_ROUTE_PATHS.detail.to, { id: commission.batch.id })}
              target="_blank"
            >
              {commission.batch.name}
            </Link>
          </>
        }
      />
    );
  } else if (viewType === "calculated" && isSplitAddedCommission(commission) && commission.splitOriginalCommission) {
    return (
      <InfoIcon
        tooltip={
          <Space>
            <span>{t("commissions.postponed.helpers.splitCreatedInBatch")}:</span>
            <div>
              <Link
                to={generatePath(COMMISSIONS_BATCH_ROUTE_PATHS.detail.to, {
                  id: commission.splitOriginalCommission.originalCommission.batch.id,
                })}
                target="_blank"
              >
                {`${commission.splitOriginalCommission.originalCommission.batch.name} | ${t("common.year")}: ${commission.splitOriginalCommission.paymentYear} (${formatLocalePercentageNumber(commission.splitOriginalCommission.paymentRatio)})`}
              </Link>
            </div>
          </Space>
        }
      />
    );
  }

  return null;
};

type ActionButtonsProps = {
  batchId: UUID;
  selectedCommissions: Commission[];
  setSelectedCommissions: (commissions: Commission[]) => void;
  onPostponeClick?: (commissions: Commission[]) => void;
};

const ActionButtons = ({
  selectedCommissions,
  onPostponeClick,
  batchId,
  setSelectedCommissions,
}: ActionButtonsProps) => {
  const { t } = useTranslation();

  const tryToIncludeCommission = useTryToIncludeCommission();

  const selectedCommissionsForPostpone = selectedCommissions.filter((commission) => !commission.postponed);
  const selectedCommissionsForInclude = selectedCommissions.filter((commission) => commission.postponed);

  return (
    <Space>
      <Button
        size="small"
        variant="outlined"
        color="orange"
        disabled={!selectedCommissionsForPostpone.length}
        icon={<AntIcon type="branch" />}
        onClick={() => {
          onPostponeClick?.(selectedCommissionsForPostpone);
          setSelectedCommissions([]);
        }}
      >
        {selectedCommissionsForPostpone.length
          ? `${t("commissions.batch.actions.postponeSelected")} (${selectedCommissionsForPostpone.length})`
          : t("commissions.batch.actions.postponeSelected")}
      </Button>

      <Popconfirm
        title={t("commissions.batch.helpers.includeSelectedCommissionsConfirm")}
        icon={<AntIcon type="question" />}
        okText={t("common.yes")}
        cancelText={t("common.no")}
        okType="primary"
        onConfirm={() => {
          tryToIncludeCommission.mutate({
            id: batchId,
            object: {
              entries: selectedCommissionsForInclude.map((commission) => ({
                id: commission.id,
                optimisticLockVersion: commission.optimisticLockVersion,
              })),
            },
          });

          setSelectedCommissions([]);
        }}
      >
        <Button
          size="small"
          variant="outlined"
          color="green"
          disabled={!selectedCommissionsForInclude.length}
          icon={<AntIcon type="pull-request" />}
        >
          {selectedCommissionsForInclude.length
            ? `${t("commissions.batch.actions.includeSelected")} (${selectedCommissionsForInclude.length})`
            : t("commissions.batch.actions.includeSelected")}
        </Button>
      </Popconfirm>
    </Space>
  );
};

interface Props {
  batch: CommissionsBatchBase;
  viewType?: "default" | "postponed" | "calculated";
  onPageChange?: (pageNumber: number) => void;
  filterData: CommissionsFilterPageRequest | PostponedCommissionsFilterPageRequest;
  institutionId?: UUID;
  commissionFormType?: "batchCommission" | "duplicatedCommission" | "postponedCommission";
}

export const BatchCommissionsTable = (props: Props) => (
  // Remounted on a step change, so that nothing selected in the previous step stays selected
  <BatchCommissionsTableContent key={props.batch.step} {...props} />
);

const BatchCommissionsTableContent = ({
  batch,
  viewType = "default",
  onPageChange,
  filterData,
  institutionId,
  commissionFormType = "batchCommission",
}: Props) => {
  const { t } = useTranslation();

  const navigate = useNavigate();

  const tryToIncludeCommission = useTryToIncludeCommission();
  const recalculateCommissionsForContract = useRecalculateCommissionsForContract();
  const deleteCommission = useDeleteCommission();
  const createDuplicatedCommission = useCreateDuplicatedCommission();
  const deleteDuplicatedCommission = useDeleteDuplicatedCommission();

  const hasManageCommissionsPermission = useHasPermissions(Permission.COMMISSIONS_MANAGE);

  const commissionsQuery = useFilterCommissions({ id: batch.id, object: filterData }, viewType !== "postponed");
  const postponedQuery = useFilterPostponedCommissions(filterData, viewType === "postponed");
  const { data } = viewType === "postponed" ? postponedQuery : commissionsQuery;

  const hasReadContractPermission = useHasAnyPermission(
    Permission.INSURANCE_READ,
    Permission.LOAN_READ,
    Permission.INVESTMENT_READ,
    Permission.DEPOSIT_READ,
    Permission.SECOND_PILLAR_READ,
    Permission.THIRD_PILLAR_READ,
    Permission.GENERIC_READ,
  );

  const [checkedCommissions, setCheckedCommissions] = useState<Commission[]>([]);
  const [commissionToUpdate, setCommissionToUpdate] = useState<Commission>();
  const [commissionsToPostpone, setCommissionsToPostpone] = useState<Commission[]>();

  const selectedCommissions = data?.pageData.length
    ? checkedCommissions.filter((commission) => data.pageData.some((c) => c.id === commission.id))
    : checkedCommissions;

  const hasActionsPermissions = batch && batch.step !== CommissionsBatchStep.FINISH && hasManageCommissionsPermission;

  const getColumns = () => {
    if (!data) {
      return [];
    }

    const columns: ColumnsType<Commission> = [
      {
        key: "duplicatedFromBatch",
        width: 35,
        render: (_, record) => <CommissionInfo commission={record} viewType={viewType} />,
      },
      {
        key: "institution",
        title: t("commissions.batch.attrs.commission.institution"),
        width: 140,
        ellipsis: { showTitle: false },
        render: (_, record) => <Ellipsis>{record.institution.name}</Ellipsis>,
      },
      {
        key: "contract",
        title: t("commissions.batch.attrs.commission.contract"),
        width: 135,
        ellipsis: { showTitle: false },
        render: (_, record) =>
          record.contract && hasReadContractPermission ? (
            <>
              {record.contractNumber && (
                <>
                  <Ellipsis tooltip={record.contractNumber}>
                    <Link to={generatePath(CONTRACT_ROUTE_PATHS.detail.to, { id: record.contract.id })} target="_blank">
                      {record.contractNumber}
                    </Link>
                  </Ellipsis>
                  <br />
                </>
              )}
              {record.secondaryContractNumber && (
                <Ellipsis tooltip={record.secondaryContractNumber}>
                  <Link to={generatePath(CONTRACT_ROUTE_PATHS.detail.to, { id: record.contract.id })} target="_blank">
                    {record.secondaryContractNumber}
                  </Link>
                </Ellipsis>
              )}
            </>
          ) : (
            <>
              {record.contractNumber && (
                <>
                  <Ellipsis>{record.contractNumber}</Ellipsis>
                  <br />
                </>
              )}
              {record.secondaryContractNumber && <Ellipsis>{record.secondaryContractNumber}</Ellipsis>}
            </>
          ),
      },
      {
        key: "product",
        title: t("commissions.batch.attrs.commission.product"),
        width: 175,
        ellipsis: { showTitle: false },
        render: (_, record) => <Ellipsis>{record.contract?.product.name}</Ellipsis>,
      },
      {
        key: "tariffCode",
        title: t("commissions.batch.attrs.commission.tariffCode"),
        width: 100,
        ellipsis: { showTitle: false },
        render: (_, record) => <Ellipsis>{record.tariffCode}</Ellipsis>,
      },
      {
        key: "clientName",
        title: t("commissions.batch.attrs.commission.clientName"),
        width: 150,
        ellipsis: { showTitle: false },
        render: (_, record) => <Ellipsis>{record.clientName}</Ellipsis>,
      },
      {
        key: "dates",
        title: t("commissions.batch.attrs.commission.dates"),
        width: 90,
        render: (_, record) => (
          <>
            {record.startDate ? formatLocaleDate(record.startDate) : undefined}
            <br />
            {record.endDate ? formatLocaleDate(record.endDate) : undefined}
          </>
        ),
      },
      {
        key: "commissionKind",
        title: t("commissions.batch.enums.commissionKind._label"),
        align: "center",
        width: 210,
        render: (_, record) => (record.commissionKind ? <CommissionKindTag kind={record.commissionKind} /> : undefined),
      },
      {
        key: "commissionBaseAmount",
        title: t("commissions.batch.attrs.commission.commissionBaseAmount"),
        align: "right",
        width: 115,
        render: (_, record) =>
          isDefined(record.commissionBaseAmount) ? formatLocaleCurrency(record.commissionBaseAmount) : undefined,
      },
      {
        key: "commissionAmount",
        title: t("commissions.batch.attrs.commission.commissionAmount"),
        align: "right",
        width: 100,
        render: (_, record) =>
          isDefined(record.commissionAmount) ? formatLocaleCurrency(record.commissionAmount) : undefined,
      },
      {
        key: "postponementReason",
        title:
          viewType === "postponed"
            ? t("commissions.postponed.helpers.originalPostponementReason")
            : t("commissions.batch.enums.postponementReason._label"),
        align: "center",
        width: 165,
        render: (_, record) =>
          viewType === "postponed" && record.type === CommissionType.DUPLICATED_COMMISSION && record.duplicateOf ? (
            <PostponementReasonTag
              reason={record.duplicateOf.postponementReason}
              manualPostponementReason={record.duplicateOf.manualPostponementReason}
            />
          ) : (
            <PostponementReasonTag
              reason={record.postponementReason}
              manualPostponementReason={record.manualPostponementReason}
            />
          ),
      },
    ];

    if (viewType === "default" && hasActionsPermissions) {
      columns.push({
        key: "actions",
        align: "right",
        fixed: "right",
        width: 270,
        render: (_, record) => (
          <>
            {record.postponed ? (
              <ActionTextIcon
                icon="pull-request"
                color="green"
                text={t("common.include")}
                disabled={batch.stepChangeInProgress || record.postponementReason === PostponementReason.IMPORT_ERROR}
                onClick={() =>
                  tryToIncludeCommission.mutate({
                    id: batch.id,
                    object: { entries: [{ id: record.id, optimisticLockVersion: record.optimisticLockVersion }] },
                  })
                }
              />
            ) : (
              <ActionTextIcon
                icon="branch"
                color="orange"
                text={t("common.postpone")}
                disabled={batch.stepChangeInProgress}
                onClick={() => setCommissionsToPostpone([record])}
              />
            )}

            <Divider orientation="vertical" />

            <ActionTextIcon
              icon="edit"
              color="blue"
              text={t("common.edit")}
              disabled={batch.stepChangeInProgress}
              onClick={() => setCommissionToUpdate(record)}
            />

            <Divider orientation="vertical" />

            <Popconfirm
              title={t("commissions.batch.helpers.deleteCommissionConfirm")}
              icon={<PopconfirmDeleteIcon />}
              okText={t("common.yes")}
              cancelText={t("common.no")}
              okType="danger"
              disabled={batch.stepChangeInProgress}
              onConfirm={() => deleteCommission.mutate({ id1: batch.id, id2: record.id })}
            >
              <ActionTextIcon
                icon="delete"
                color="red"
                text={t("common.delete")}
                disabled={batch.stepChangeInProgress}
              />
            </Popconfirm>
          </>
        ),
      });
    } else if (viewType === "postponed" && hasActionsPermissions) {
      columns.push({
        key: "actions",
        align: "right",
        fixed: "right",
        width:
          (data as PostponedCommissionsFilterPageResult).report ===
          PostponedCommissionsReport.UNRESOLVED_INCLUDED_IN_BATCH
            ? 180
            : 95,
        render: (_, record) =>
          (data as PostponedCommissionsFilterPageResult).report ===
          PostponedCommissionsReport.UNRESOLVED_INCLUDED_IN_BATCH ? (
            <>
              <ActionTextIcon
                icon="branch"
                color="orange"
                text={t("common.exclude")}
                disabled={batch.stepChangeInProgress}
                onClick={() => deleteDuplicatedCommission.mutate({ id1: batch.id, id2: record.id })}
              />

              <Divider orientation="vertical" />

              <ActionTextIcon
                icon="edit"
                color="blue"
                text={t("common.edit")}
                disabled={batch.stepChangeInProgress}
                onClick={() => setCommissionToUpdate(record)}
              />
            </>
          ) : (
            <ActionTextIcon
              icon="pull-request"
              color="green"
              text={t("common.include")}
              disabled={batch.stepChangeInProgress}
              onClick={() =>
                createDuplicatedCommission.mutate({
                  id: batch.id,
                  object: { postponedId: record.id, postponedOptimisticLockVersion: record.optimisticLockVersion },
                })
              }
            />
          ),
      });
    } else if (viewType === "calculated") {
      columns.push({
        key: "statusTag",
        align: "center",
        width: 80,
        render: (_, record) =>
          (!!record.errors.length ||
            record.calculatedCommissions.some((c) => !!c.errors.length) ||
            record.splitCommissions?.some(
              (sc) => !!sc.errors.length || sc.calculatedCommissions.some((scc) => !!scc.errors.length),
            )) && <StatusTag status="error" tooltip={t("commissions.batch.helpers.calculationError")} />,
      });

      if (hasActionsPermissions) {
        columns.push({
          key: "actions",
          align: "right",
          fixed: "right",
          width: 375,
          render: (_, record) => {
            const recalculationDisabled = batch.stepChangeInProgress || !record.contract;

            return (
              <>
                {record.postponed ? (
                  <ActionTextIcon
                    icon="pull-request"
                    color="green"
                    text={t("common.include")}
                    disabled={
                      batch.stepChangeInProgress || record.postponementReason === PostponementReason.IMPORT_ERROR
                    }
                    onClick={() =>
                      tryToIncludeCommission.mutate({
                        id: batch.id,
                        object: { entries: [{ id: record.id, optimisticLockVersion: record.optimisticLockVersion }] },
                      })
                    }
                  />
                ) : (
                  <>
                    <ActionTextIcon
                      icon="branch"
                      color="orange"
                      text={t("common.postpone")}
                      disabled={batch.stepChangeInProgress}
                      onClick={() => setCommissionsToPostpone([record])}
                    />

                    <Divider orientation="vertical" />

                    <Popconfirm
                      title={
                        <>
                          <p style={{ maxWidth: 250 }}>{t("commissions.batch.helpers.recalculationConfirmQuestion")}</p>
                          <p style={{ maxWidth: 250, marginBottom: 0 }}>
                            {t("commissions.batch.helpers.recalculationConfirmClarification")}
                          </p>
                        </>
                      }
                      icon={<AntIcon type="question" />}
                      okText={t("common.yes")}
                      cancelText={t("common.no")}
                      okType="primary"
                      disabled={recalculationDisabled}
                      onConfirm={() =>
                        recalculateCommissionsForContract.mutate({
                          id: batch.id,
                          object: { contractId: record.contract?.id ?? "" },
                        })
                      }
                    >
                      <ActionTextIcon
                        icon="reload"
                        color="green"
                        text={t("common.recalculate")}
                        disabled={recalculationDisabled}
                      />
                    </Popconfirm>
                  </>
                )}

                <Divider orientation="vertical" />

                <ActionTextIcon
                  icon="edit"
                  color="blue"
                  text={t("common.edit")}
                  disabled={batch.stepChangeInProgress}
                  onClick={() => setCommissionToUpdate(record)}
                />

                <Divider orientation="vertical" />

                <Popconfirm
                  title={t("commissions.batch.helpers.deleteCommissionConfirm")}
                  icon={<PopconfirmDeleteIcon />}
                  okText={t("common.yes")}
                  cancelText={t("common.no")}
                  okType="danger"
                  disabled={batch.stepChangeInProgress}
                  onConfirm={() => deleteCommission.mutate({ id1: batch.id, id2: record.id })}
                >
                  <ActionTextIcon
                    icon="delete"
                    color="red"
                    text={t("common.delete")}
                    disabled={batch.stepChangeInProgress}
                  />
                </Popconfirm>
              </>
            );
          },
        });
      }
    }

    return columns;
  };

  return (
    <>
      {data?.commissionAmountsSum ? (
        <div className="margin-bottom-small">
          <b>{t("commissions.batch.helpers.filteredCommissionsSum")}: </b>

          {formatLocaleCurrency(data.commissionAmountsSum)}
        </div>
      ) : undefined}

      {batch && viewType === "calculated" && hasActionsPermissions && data?.pageData.length ? (
        <div className="table-header-margin">
          <ActionButtons
            selectedCommissions={selectedCommissions}
            setSelectedCommissions={setCheckedCommissions}
            batchId={batch?.id}
            onPostponeClick={setCommissionsToPostpone}
          />
        </div>
      ) : undefined}

      <Table<Commission>
        {...tableStandardProps()}
        columns={getColumns()}
        scroll={{ x: TableSizes.XXL }}
        dataSource={data?.pageData}
        rowSelection={
          hasActionsPermissions
            ? {
                onChange: (_, selectedRows) => setCheckedCommissions(selectedRows),
                selectedRowKeys: selectedCommissions.map((commission) => commission.id),
              }
            : undefined
        }
        expandable={{
          rowExpandable: (record) =>
            ((data as PostponedCommissionsFilterPageResult).report !==
              PostponedCommissionsReport.UNRESOLVED_INCLUDED_IN_BATCH &&
              !!record.errors.length) ||
            (viewType === "calculated" && !!record.calculatedCommissions.length),
          expandedRowRender: (record) => (
            <BatchCommissionExtendedTable
              commission={record}
              showCalculations={viewType === "calculated"}
              batchId={batch.id}
            />
          ),
        }}
        pagination={{
          ...paginationTableProps,
          current: numberOrZero(data?.pageIndex) + 1,
          pageSize: data?.pageSize,
          total: data?.totalElementsCount,
          onChange: (pageIndex) => {
            setCheckedCommissions([]);

            if (onPageChange) {
              onPageChange(pageIndex);
            } else {
              navigate({ search: URLQuery.getTableChangePageIndex(pageIndex) }, { replace: true });
            }
          },
        }}
      />

      <CommissionForm
        open={!!commissionToUpdate}
        formType={commissionFormType}
        commission={commissionToUpdate}
        batchId={batch.id}
        institutionId={institutionId}
        onFormCancel={() => setCommissionToUpdate(undefined)}
      />

      <CommissionPostponeForm
        commissions={commissionsToPostpone}
        batchId={batch.id}
        open={!!commissionsToPostpone}
        onFormCancel={() => setCommissionsToPostpone(undefined)}
      />
    </>
  );
};
